import { createAuthClient } from "better-auth/react";

const API_BASE = import.meta.env.PROD
  ? 'https://zeropointdomains.com'
  : 'http://localhost:3000';

export const authClient = createAuthClient({
  baseURL: API_BASE,
  fetchOptions: {
    credentials: "include", // Ensure cookies are sent with requests
  },
});

// Export typed hooks and methods
export const {
  signIn,
  signUp,
  signOut,
  useSession,
  getSession,
} = authClient;

// Helper functions for password management
// These call the auth API directly since BetterAuth client doesn't expose them
export async function forgetPassword(data: { email: string; redirectTo?: string }) {
  const response = await fetch(`${API_BASE}/api/auth/forget-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  const result = await response.json();
  if (!response.ok) {
    return { error: { message: result.message || 'Request failed' } };
  }
  return { data: result };
}

export async function resetPassword(data: { newPassword: string; token: string }) {
  const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  const result = await response.json();
  if (!response.ok) {
    return { error: { message: result.message || 'Reset failed' } };
  }
  return { data: result };
}

export async function changePassword(data: { currentPassword: string; newPassword: string; revokeOtherSessions?: boolean }) {
  const response = await fetch(`${API_BASE}/api/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  const result = await response.json();
  if (!response.ok) {
    return { error: { message: result.message || 'Change failed' } };
  }
  return { data: result };
}

export async function sendVerificationEmail(data: { email: string; callbackURL?: string }) {
  const response = await fetch(`${API_BASE}/api/auth/send-verification-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  const result = await response.json();
  if (!response.ok) {
    return { error: { message: result.message || 'Failed to send verification email' } };
  }
  return { data: result };
}

// Types for session
export interface User {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  image: string | null;
  isAdmin: boolean;
  twoFactorEnabled: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Session {
  id: string;
  userId: string;
  token: string;
  expiresAt: Date;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface AuthSession {
  user: User;
  session: Session;
}
